import { JSX } from "react";
import { useTranslation } from "react-i18next";

import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { FormField } from "lib/components/form/FormTypes";
import ImageUpload from "lib/components/form/ImageUpload";
import Select from "lib/components/form/Select";
import TextInput from "lib/components/form/TextInput";

import { asTitle } from "utils/Utils";

import { OrganisationType } from "../OrganisationTypes";

export type OrganisationInfoSectionProps = {
	name: FormField<string>;
	slogan: FormField<string>;
	description: FormField<string>;
	smallLogo: FormField<string>;
	largeLogo: FormField<string>;
	type: FormField<string>;
	vatIdentification: FormField<string>;
	email: FormField<string>;
	phoneNumber: FormField<string>;
};

export default function OrganisationInfoSection(
	props: Readonly<OrganisationInfoSectionProps>
): JSX.Element {
	const { t } = useTranslation();

	const theme = useTheme();

	return (
		<>
			<Typography variant="h6">{asTitle(t("general"))}</Typography>
			<Grid container direction="column" rowSpacing={2}>
				<Grid item container columnSpacing={2}>
					<Grid item xs={12} md={6}>
						<TextInput
							name="organisationName"
							label={asTitle(t("name"))}
							field={props.name}
						/>
					</Grid>
				</Grid>
				<Grid item container>
					<Grid item xs={12} md={6}>
						<TextInput
							name="organisationSlogan"
							label={asTitle(t("slogan"))}
							field={props.slogan}
						/>
					</Grid>
				</Grid>
				<Grid item container>
					<Grid item xs={12} md={6}>
						<TextInput
							name="organisationDescription"
							label={asTitle(t("description"))}
							field={props.description}
						/>
					</Grid>
				</Grid>

				<Grid item container>
					<Grid item xs={12} sm={6} md={3}>
						<ImageUpload
							name="smallLogo"
							label={asTitle(t("smallLogo"))}
							field={props.smallLogo}
							height={32}
							width={32}
							background={theme.palette.primary.main}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<ImageUpload
							name="largeLogo"
							label={asTitle(t("largeLogo"))}
							field={props.largeLogo}
							height={32}
							width={160}
							background={theme.palette.primary.main}
						/>
					</Grid>
				</Grid>

				<Grid item container columnSpacing={2}>
					<Grid item xs={12} sm={6} md={3}>
						<Select
							name="organisationType"
							label={asTitle(t("type"))}
							field={props.type}
							options={Object.values(OrganisationType).map(
								type => [type, asTitle(t(type))]
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<TextInput
							name="vatIdentification"
							label={asTitle(t("vatIdentification"))}
							field={props.vatIdentification}
						/>
					</Grid>
				</Grid>

				<Grid item container columnSpacing={2}>
					<Grid item xs={12} sm={6} md={3}>
						<TextInput
							name="email"
							label={asTitle(t("email"))}
							field={props.email}
							type="email"
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<TextInput
							name="phoneNumber"
							label={asTitle(t("phoneNumber"))}
							field={props.phoneNumber}
							type="tel"
						/>
					</Grid>
				</Grid>
			</Grid>
		</>
	);
}
